import { ChangeDetectorRef, Component, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import {ExpenseApproval} from "../expense-approval.model";
import {Customer} from "../../customer/customer.model";
import {ChartOfAccounts} from "../../chart-of-accounts/chart-of-accounts.model";
import {ExpenseService} from "../expense.service";
import {SessionStorageService} from "../../services/session-storage.service";
import {Expense} from "../expense.model";
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TextareaModule } from 'primeng/textarea';
import { ProgressBarModule } from "primeng/progressbar";


@Component({
    selector: 'app-expense-approval',
    templateUrl: './expense-approval.component.html',
    styleUrls: ['./expense-approval.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ToastModule,
    ButtonModule,
    TableModule,
    ToolbarModule,
    BreadcrumbModule,
    InputTextModule,
    CardModule,
    TagModule,
    DialogModule,
    ConfirmDialogModule,
    TextareaModule,
    ProgressBarModule
],
    providers: [
        MessageService,
        ExpenseService,
        SessionStorageService
    ]
})
export class ExpenseApprovalComponent implements OnInit {
    private readonly ngZone = inject(NgZone);
    private readonly cdr = inject(ChangeDetectorRef);
    @ViewChild('dt') dt: Table | undefined;

    expensesToApprove: Expense[] = [];
    filteredExpenses: Expense[] = []; // For local filtering display if needed
    isLoading: boolean = false;
    totalRecords: number = 0;
    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    displayApprovalDialog: boolean = false;
    selectedApproval: ExpenseApproval | undefined;
    selectedExpense: Expense | undefined;
    approvalComments: string = '';
    currentAction: 'Approved' | 'Rejected' | '' = '';

    currentApproverId: number;

    // Mocked data for hydration (same as other components)
    mockCustomers: Customer[] = [];
    mockChartOfAccounts: ChartOfAccounts[] = [];

    constructor(
        private expenseService: ExpenseService,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService
    ) { }

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Expenses' }, { label: 'Approvals' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.currentApproverId = this.sessionStorageService.getCustomer()?.id ?? 0;
    }

    private syncView(work?: () => void): void {
        this.ngZone.run(() => {
            work?.();
            this.cdr.detectChanges();
        });
    }

    loadApprovalsLazy(event: any): void {
        this.isLoading = true;
        this.expenseService.getPendingApprovalsForApprover(this.currentApproverId).subscribe({
            next: (data: Expense[]) => {
                this.syncView(() => {
                    this.expensesToApprove = data;
                    this.totalRecords = this.expensesToApprove.length;
                    this.isLoading = false;
                    this.messageService.add({severity: 'success', summary: 'Loaded', detail: `${this.totalRecords} approvals loaded.`});
                });
            },
            error: (error) => {
                this.syncView(() => {
                    this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to load approvals.'});
                    this.isLoading = false;
                });
                console.error('Error fetching approvals:', error);
            }
        });
    }

    getStatusSeverity(status: string): string {
        switch (status) {
            case 'Approved':
                return 'success';
            case 'Pending':
                return 'warning';
            case 'Rejected':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    openApprovalDialog(expense: Expense, action: 'Approved' | 'Rejected'): void {
        this.selectedExpense = expense;
        this.currentAction = action;
        this.approvalComments = ''; // Clear previous comments
        this.displayApprovalDialog = true;
    }

    confirmApprovalAction(): void {
        this.selectedApproval = this.selectedExpense?.approvals?.find(approval => approval.approver.id === this.currentApproverId);
        if (this.selectedExpense && this.selectedApproval) {
            this.isLoading = true;
            this.expenseService.updateApprovalStatus(this.selectedApproval?.approvalId ?? 0, this.currentAction, this.approvalComments).subscribe({
                next: (response) => {
                    this.syncView(() => {
                        this.messageService.add({severity: 'success', summary: 'Success', detail: `Expense ${this.currentAction} successfully.`});
                        this.displayApprovalDialog = false;
                        this.isLoading = false;
                    });
                    this.dt?.reset(); // Reload table data
                },
                error: (error) => {
                    this.syncView(() => {
                        this.messageService.add({severity: 'error', summary: 'Error', detail: `Failed to ${this.currentAction} expense.`});
                        this.isLoading = false;
                    });
                    console.error(`Error ${this.currentAction} expense:`, error);
                }
            });
        } else {
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'No approval selected.'});
        }
    }

    /**
     * Applies a global filter to the table, safely accessing the input's value.
     * This method is called from the template to resolve strict typing errors.
     * @param $event The input event from the search field.
     * @param stringVal The type of filter match (e.g., 'contains').
     */
    applyFilterGlobal($event: Event, stringVal: string) {
        this.dt!.filterGlobal(($event.target as HTMLInputElement).value, stringVal);
    }
}
